import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Link, Typography } from '@mui/material';
import { DisclosureTooltip } from '../DisclosureTooltip';
import { StarIcon } from './components/StarIcon';

/** Same as `palette.greens.main` / `palette.neutral.lighter`. StarIcon paints via raw `background` (see StarIcon.tsx), which does not resolve MUI palette paths — pass hex. */
const RATED_STAR_FILL = '#008254';
const EMPTY_STAR_FILL = '#D8D9DA';

type Props = {
  starRating?: number;
  starRatingTooltip?: string;
  reviewLink?: string;
  displayLabel?: boolean;
};

export const SingleStarLayout = ({
  displayLabel = false,
  starRating,
  starRatingTooltip,
  reviewLink,
}: Props) => {
  if (!starRating)
    return (
      <Typography variant="textSmall" sx={{ display: 'block', color: 'neutral.dark' }}>
        <StarIcon
          color={EMPTY_STAR_FILL}
          additionalStyles={{
            display: 'inline-block',
            width: 20,
            height: 20,
            verticalAlign: 'bottom',
          }}
        />{' '}
        Not yet rated
      </Typography>
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}>
      {displayLabel && (
        <Typography variant="textSmall" sx={{ color: 'text.secondary' }}>
          NerdWallet rating
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 0.5 }}>
        <Typography
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          <StarIcon
            color={RATED_STAR_FILL}
            additionalStyles={{
              width: 20,
              height: 20,
              mb: '2px',
            }}
          />
          {reviewLink && starRating ? (
            <Link
              href={reviewLink}
              target="_blank"
              rel="noopener"
              sx={{ typography: { xs: 'bodyLarge', md: 'bodyExtraLarge' } }}
            >
              {starRating?.toFixed(1)}
            </Link>
          ) : (
            starRating?.toFixed(1)
          )}
          {starRatingTooltip && (
            <DisclosureTooltip
              tooltipStyleOverride={{
                lineHeight: 1,
                maxWidth: '265px',
              }}
              trigger={
                <Link
                  aria-label="NerdWallet rating info"
                  component="button"
                  color="text.primary"
                  variant="text"
                  lineHeight={1}
                  sx={{
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <HelpOutlineIcon sx={{ color: 'neutral.dark' }} />
                </Link>
              }
            >
              <Typography variant="textSmall" sx={{ color: 'neutral.darkest' }}>
                {starRatingTooltip}
              </Typography>
            </DisclosureTooltip>
          )}
        </Typography>
      </Box>
    </Box>
  );
};
